(() => {
  'use strict';
  const cfg = window.OutlawTheme || {};
  const settings = cfg.settings || {};
  const routes = cfg.routes || {};
  const state = { cart: null, busy: false, cartPageDirty: false, purchaseMode: 'once', purchaseFrequency: '', dialogSourceForm: null, rewardTier: 1, pendingAdds: [], nextPendingAdd: 0, addFlushTimer: null, addQueue: Promise.resolve() };
  let cartReturnFocus = null;
  let rewardReturnFocus = null;
  let menuReturnFocus = null;
  let lockedScrollY = 0;
  const overlayClasses = ['menu-open', 'cart-open', 'reward-dialog-open', 'product-dialog-open'];
  const syncPageScrollLock = () => {
    const locked = overlayClasses.some((className) => document.body.classList.contains(className));
    const wasLocked = document.body.classList.contains('page-scroll-locked');
    if (locked && !wasLocked) {
      lockedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.setProperty('--outlaw-lock-offset', `-${lockedScrollY}px`);
      document.body.classList.add('page-scroll-locked');
      document.documentElement.classList.add('page-scroll-locked');
    } else if (!locked && wasLocked) {
      document.body.classList.remove('page-scroll-locked');
      document.documentElement.classList.remove('page-scroll-locked');
      document.body.style.removeProperty('--outlaw-lock-offset');
      window.scrollTo(0, lockedScrollY);
    }
  };
  const setOverlayOpen = (className, open) => {
    document.body.classList.toggle(className, open);
    document.documentElement.classList.toggle(className, open);
    syncPageScrollLock();
  };
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const focusableNodes = (root) => $$('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', root).filter((node) => !node.hidden && node.getClientRects().length > 0);
  const trapFocus = (event, root) => {
    if (event.key !== 'Tab') return;
    const nodes = focusableNodes(root); if (!nodes.length) return;
    const first = nodes[0]; const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  const money = (cents) => {
    const value = Number(cents || 0) / 100;
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value); }
    catch { return `$${value.toFixed(2)}`; }
  };
  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));
  const toast = (message) => {
    const node = $('[data-outlaw-toast]'); if (!node) return;
    node.textContent = message; node.hidden = false;
    window.clearTimeout(toast.timer); toast.timer = window.setTimeout(() => { node.hidden = true; }, 3600);
  };
  const errorText = (error) => error?.description || error?.message || 'Something went wrong. Please try again.';
  const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  const setCartError = (message = '') => {
    const node = $('[data-cart-error]');
    if (node) { node.textContent = message; node.hidden = !message; }
  };
  const selectedPrice = (data, planId, subscribed) => {
    if (subscribed && planId) {
      const entry = String(data.planPrices || '').split('|').find((item) => item.split(':')[0] === String(planId));
      if (entry) {
        try { return decodeURIComponent(entry.slice(entry.indexOf(':') + 1).replace(/\+/g, ' ')); } catch (_) {}
      }
    }
    return data.price || '';
  };
  const planCatalog = cfg.planCatalog || {};
  const planOptions = (variantId) => Array.isArray(planCatalog[String(variantId)]) ? planCatalog[String(variantId)] : [];
  const normalizePlanName = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const planForVariant = (variantId, preferredName = '') => {
    const options = planOptions(variantId);
    if (!options.length) return null;
    const normalized = normalizePlanName(preferredName);
    return options.find((plan) => normalizePlanName(plan.name) === normalized) || options[0];
  };
  const activeLine = (line) => Number(line?.quantity || 0) > 0;
  const activeCartItems = (cart) => (cart?.items || []).filter(activeLine);
  const staleCartItems = (cart) => (cart?.items || []).filter((line) => !activeLine(line));
  const normalizedLineProperties = (line) => Object.keys(line?.properties || {}).sort().reduce((properties, key) => {
    const value = line.properties[key];
    if (value !== null && value !== undefined && value !== '') properties[key] = value;
    return properties;
  }, {});
  const cartRestoreItem = (line) => {
    const item = { id: Number(line.variant_id), quantity: Number(line.quantity), properties: normalizedLineProperties(line) };
    const sellingPlan = line.selling_plan_allocation?.selling_plan?.id;
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    return item;
  };
  const cartLineIdentity = (line) => JSON.stringify([
    Number(line.variant_id),
    Number(line.selling_plan_allocation?.selling_plan?.id || line.selling_plan || 0),
    normalizedLineProperties(line)
  ]);
  const cartLineQuantities = (lines) => lines.reduce((totals, line) => {
    const identity = cartLineIdentity(line);
    totals.set(identity, (totals.get(identity) || 0) + Number(line.quantity || 0));
    return totals;
  }, new Map());
  const sameActiveCartContents = (expectedLines, cart) => {
    const expected = cartLineQuantities(expectedLines);
    const actual = cartLineQuantities(activeCartItems(cart));
    return expected.size === actual.size && Array.from(expected).every(([identity, quantity]) => actual.get(identity) === quantity);
  };
  const subscriptionLine = (line) => activeLine(line) && !rewardLine(line) && planOptions(line.variant_id).length > 0;
  const subscriptionLines = (cart) => activeCartItems(cart).filter(subscriptionLine);
  const planNamesForCart = (cart) => {
    const lines = subscriptionLines(cart);
    if (!lines.length) return [];
    const names = new Map(planOptions(lines[0].variant_id).map((plan) => [normalizePlanName(plan.name), String(plan.name)]).filter(([key]) => key));
    lines.slice(1).forEach((line) => {
      const available = new Set(planOptions(line.variant_id).map((plan) => normalizePlanName(plan.name)));
      Array.from(names.keys()).forEach((key) => { if (!available.has(key)) names.delete(key); });
    });
    return Array.from(names, ([value, label]) => ({ value, label }));
  };
  const setProductImage = (image, src, alt) => {
    if (!image || !src) return;
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = src;
    if (alt) image.alt = alt;
    const zoom = image.closest('.product-gallery')?.querySelector('.gallery-zoom');
    if (zoom) {
      try {
        const fullSize = new URL(src, window.location.href);
        fullSize.searchParams.set('width', '2000');
        zoom.href = fullSize.toString();
      } catch (_) { zoom.href = src; }
    }
  };
  const fetchJSON = async (url, options = {}) => {
    const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...(options.headers || {}) }, ...options });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw Object.assign(new Error(body.description || body.message || 'Request failed'), body);
    return body;
  };
  const rewardLine = (line) => String(line.properties?._outlaw_reward || '') === 'true';
  // Legacy reward lines did not carry a tier. Treat them as the first reward so
  // existing carts remain valid when the twelve-can tier launches.
  const rewardTier = (line) => rewardLine(line) ? (String(line.properties?._outlaw_reward_tier || '1') === '2' ? 2 : 1) : 0;
  const rewardLines = (cart, tier = 0) => activeCartItems(cart).filter((line) => rewardLine(line) && (!tier || rewardTier(line) === Number(tier)));
  // Shopify product IDs from the configured single-can collection are the source
  // of eligibility. Pack size, titles and editable line properties cannot qualify
  // a prepacked bundle for this promotion.
  const rewardProductIds = new Set((cfg.rewardProductIds || []).map(String));
  // The reward picker accepts every available variant in the configured
  // single-can collection. Bundles stay out because they are not in this list.
  const rewardVariantIds = new Set((cfg.rewardVariantIds || []).map(String));
  const bundleCanCounts = {
    'outlaw-fine-cut-sampler-pack': 4,
    'reloads-pouches-sampler-8-pack': 8,
    'berserker-caffeine-pouches-sampler-12-pack': 12,
    'outlaw-ogs-fat-cut-bundle-1': 6,
    'best-selling-fat-cut-bundle': 6
  };
  const lineCanCount = (line) => {
    if (rewardLine(line)) return 0;
    if (bundleCanCounts[line.handle]) return bundleCanCounts[line.handle] * line.quantity;
    const explicit = Number(line.properties?._can_count);
    if (Number.isFinite(explicit) && explicit > 0) return explicit * line.quantity;
    const label = `${line.product_title || ''} ${line.variant_title || ''}`;
    const pack = label.match(/(?:^|\s)(3|4|5|6|8|10|12)[- ]?(?:can|pack)/i);
    return (pack ? Number(pack[1]) : 1) * line.quantity;
  };
  const lineFormatLabel = (line) => {
    if (rewardLine(line)) return line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title : 'Single can';
    const explicit = Number(line.properties?._can_count);
    const packSize = Number(bundleCanCounts[line.handle] || (Number.isFinite(explicit) && explicit > 1 ? explicit : 0));
    if (packSize > 1) return `${packSize}-CAN VARIETY PACK`;
    return line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title : 'Single can';
  };
  const linePurchaseMeta = (line) => [
    lineFormatLabel(line),
    rewardLine(line) ? 'ONE-TIME REWARD' : line.selling_plan_allocation ? 'SUBSCRIBE' : 'ONE-TIME'
  ].join(' · ');
  const rewardVariantText = (line) => {
    const label = line?.variant_title && line.variant_title !== 'Default Title' ? String(line.variant_title).trim() : '';
    return /\b\d+\s*mg\b/i.test(label) ? label : '';
  };
  const rewardDisplayName = (line) => line ? `${line.product_title}${rewardVariantText(line) ? ` (${rewardVariantText(line)})` : ''}` : '';
  const canCountFromLabel = (label) => {
    const pack = String(label || '').match(/(?:^|\s)(3|4|5|6|8|10|12)[- ]?(?:can|pack)/i);
    if (pack) return Number(pack[1]);
    return /(?:^|\s)single(?:\s|$)/i.test(String(label || '')) ? 1 : 0;
  };
  const paidCans = (cart) => activeCartItems(cart).reduce((sum, line) => sum + lineCanCount(line), 0);
  // Eligible paid single cans unlock the reward whether they are one-time or
  // subscription purchases. The reward row itself is always added without a
  // selling plan, and prepacked bundles remain outside rewardProductIds.
  const rewardEligibleCans = (cart) => activeCartItems(cart).reduce((sum, line) => sum + (!rewardLine(line) && rewardProductIds.has(String(line.product_id)) ? line.quantity : 0), 0);
  const rewardThreshold = (tier = 1) => Number(Number(tier) === 2 ? settings.secondFreeCanAt || 12 : settings.freeCanAt || 6);
  const rewardEnabled = (tier = 1) => Number(tier) === 2 ? Boolean(settings.secondFreeCanEnabled) : Boolean(settings.freeCanEnabled);
  const selectedReward = (cart, tier = 1) => rewardLines(cart, tier)[0] || null;
  const rewardUnlocked = (cart, tier = 1) => Boolean(rewardEnabled(tier) && rewardEligibleCans(cart) >= rewardThreshold(tier));
  const rewardRequired = (cart, tier = 1) => rewardUnlocked(cart, tier) && !selectedReward(cart, tier);
  const rewardIsEligible = (cart) => {
    const rewards = rewardLines(cart);
    return rewards.every((reward) => rewardUnlocked(cart, rewardTier(reward)) && rewardVariantIds.has(String(reward.variant_id)) && Number(reward.quantity) === 1 && (settings.rewardDiscountCode || Number(reward.final_line_price) === 0))
      && [1, 2].every((tier) => rewardLines(cart, tier).length <= 1);
  };
  const nextRequiredRewardTier = (cart) => [1, 2].find((tier) => rewardRequired(cart, tier)) || 0;
  const canCheckout = (cart) => paidCans(cart) >= Number(settings.minimumCans || 3) && !nextRequiredRewardTier(cart) && rewardIsEligible(cart);
  const progressMessage = (cart) => {
    const count = paidCans(cart); const eligibleCount = rewardEligibleCans(cart);
    const minimum = Number(settings.minimumCans || 3);
    const rewardAt = Number(settings.freeCanAt || 6);
    const secondRewardAt = Number(settings.secondFreeCanAt || 12);
    const shippingAt = Number(settings.freeShippingAt || 10);
    if (count < minimum) return `Add ${minimum - count} more to unlock checkout`;
    if (rewardRequired(cart, 1)) return 'Choose your first free can';
    const rewardRemaining = Math.max(0, rewardAt - eligibleCount);
    const shippingRemaining = Math.max(0, shippingAt - count);
    if (settings.freeShippingEnabled && shippingRemaining > 0 && (!settings.freeCanEnabled || shippingRemaining <= rewardRemaining)) return `Add ${shippingRemaining} more for free priority shipping`;
    if (settings.freeCanEnabled && eligibleCount < rewardAt) {
      return `Add ${rewardRemaining} more to unlock free can`;
    }
    if (settings.freeShippingEnabled && shippingRemaining > 0) return `Add ${shippingRemaining} more for free priority shipping`;
    if (rewardRequired(cart, 2)) return 'Choose your second free can';
    if (settings.secondFreeCanEnabled && eligibleCount < secondRewardAt) {
      const remaining = secondRewardAt - eligibleCount;
      return `ONLY ${remaining} CAN${remaining === 1 ? '' : 'S'} AWAY FROM ANOTHER FREE CAN!`;
    }
    if (settings.freeShippingEnabled && settings.secondFreeCanEnabled) return 'Both free cans and free priority shipping unlocked';
    if (settings.freeShippingEnabled) return rewardUnlocked(cart, 1) && selectedReward(cart, 1) ? 'Checkout, free can and free priority shipping unlocked' : 'Checkout and free priority shipping unlocked';
    return 'Checkout unlocked';
  };
  const progressPercent = (count) => Math.min(100, count / Math.max(Number(settings.freeShippingAt || 10), settings.secondFreeCanEnabled ? Number(settings.secondFreeCanAt || 12) : 0) * 100);
  const getCart = async () => {
    const url = `${routes.cart || '/cart'}.js`;
    const separator = url.includes('?') ? '&' : '?';
    state.cart = await fetchJSON(`${url}${separator}_outlaw_refresh=${Date.now()}`, { cache: 'no-store' });
    return state.cart;
  };
  const sanitizeCartForCheckout = async () => {
    let cart = await getCart();

    // Shopify's Ajax cart omits some zero-quantity bundle records even though
    // checkout still receives them. Because those ghosts cannot be detected
    // here, rebuild the positive cart on every checkout and verify it before
    // leaving the storefront. This preserves rewards, private properties and
    // selling plans while making invisible stale records impossible to carry
    // into checkout.
    const expectedLines = activeCartItems(cart).map((line) => ({ ...line, properties: normalizedLineProperties(line) }));
    const items = expectedLines.map(cartRestoreItem);
    await fetchJSON(routes.cartClear || '/cart/clear.js', { method: 'POST', body: JSON.stringify({}) });
    if (items.length) {
      try {
        await fetchJSON(routes.cartAdd || '/cart/add.js', { method: 'POST', body: JSON.stringify({ items }) });
      } catch (_) {
        // Shopify normally handles the batch atomically. Clear again before a
        // line-by-line recovery so a partial response can never duplicate an
        // item, then keep every line that remains available for review.
        await fetchJSON(routes.cartClear || '/cart/clear.js', { method: 'POST', body: JSON.stringify({}) });
        for (const item of items) {
          try { await fetchJSON(routes.cartAdd || '/cart/add.js', { method: 'POST', body: JSON.stringify({ items: [item] }) }); }
          catch (_) {}
        }
      }
    }
    cart = await getCart();
    if (staleCartItems(cart).length || !sameActiveCartContents(expectedLines, cart)) {
      throw new Error('Your stash was cleaned, but one or more items changed availability. Please review the cart before checkout.');
    }
    state.cartPageDirty = true;
    return { cart, cleaned: true };
  };
  const optimisticCart = (cart = state.cart) => {
    const base = cart || { items: [], item_count: 0, total_price: 0 };
    if (!state.pendingAdds.length) return base;
    const optimistic = { ...base, items: activeCartItems(base).map((line) => ({ ...line, properties: { ...(line.properties || {}) } })) };
    state.pendingAdds.forEach((pending) => {
      const visibleQuantity = Math.max(0, Number(pending.quantity || 0) - Number(pending.undoQuantity || 0));
      if (!visibleQuantity) return;
      const match = optimistic.items.find((line) => !rewardLine(line) && String(line.variant_id) === String(pending.variantId) && String(line.selling_plan_allocation?.selling_plan?.id || '') === String(pending.sellingPlan || ''));
      if (match) {
        match.quantity += visibleQuantity;
        match.final_line_price = Number(match.final_line_price || 0) + Number(pending.priceCents || 0) * visibleQuantity;
      } else {
        optimistic.items.push({
          key: `pending-${pending.token}`,
          variant_id: Number(pending.variantId),
          product_id: Number(pending.productId || 0),
          product_title: pending.productTitle || 'Outlaw single can',
          variant_title: pending.variantTitle || 'Single can',
          quantity: visibleQuantity,
          final_line_price: Number(pending.priceCents || 0) * visibleQuantity,
          image: pending.image || '',
          url: pending.url || '#',
          properties: { ...(pending.properties || {}) },
          selling_plan_allocation: pending.sellingPlan ? { selling_plan: { id: Number(pending.sellingPlan), name: pending.sellingPlanName || 'Subscribe & Save' } } : null,
          _outlaw_pending: true
        });
      }
      optimistic.item_count = Number(optimistic.item_count || 0) + visibleQuantity;
      optimistic.total_price = Number(optimistic.total_price || 0) + Number(pending.priceCents || 0) * visibleQuantity;
    });
    return optimistic;
  };
  // The full cart is server-rendered; reload it after drawer mutations so its
  // quantities, reward rows and subtotal cannot disagree with the Ajax cart.
  const refreshFullCartPage = () => {
    if (!state.cartPageDirty || !document.body.classList.contains('template-cart')) return false;
    window.location.reload();
    return true;
  };

  const updateProgress = (cart) => {
    const count = paidCans(cart); const message = progressMessage(cart);
    $$('[data-cart-count]').forEach((n) => { n.textContent = cart.item_count || 0; });
    $$('[data-paid-count],[data-drawer-paid-count]').forEach((n) => { n.textContent = count; });
    $$('[data-next-message],[data-drawer-next]').forEach((n) => { n.textContent = message; });
    $$('[data-progress-bar],[data-drawer-progress-bar]').forEach((n) => { n.style.width = `${progressPercent(count)}%`; });
    $$('[data-mark]').forEach((n) => {
      const reached = n.hasAttribute('data-reward-mark') ? rewardUnlocked(cart, Number(n.dataset.rewardMark || 1)) : count >= Number(n.dataset.mark);
      n.classList.toggle('reached', reached);
      if (n.hasAttribute('data-journey-step')) {
        const marker = $('[data-journey-marker]', n);
        if (marker) marker.textContent = reached ? '✓' : (n.dataset.stepLabel || '');
      }
    });
    const global = $('[data-global-progress]');
    const builderPage = Boolean($('.pack-page'));
    if (global) global.hidden = builderPage || !(count > 0 || document.body.classList.contains('template-product'));
    const drawerProgress = $('[data-cart-progress]'); if (drawerProgress) drawerProgress.hidden = !activeCartItems(cart).length;
  };

  const setPurchaseStatus = (message = '', error = false) => {
    $$('[data-purchase-status]').forEach((node) => {
      node.textContent = message;
      node.hidden = !message;
      node.classList.toggle('error', Boolean(error));
    });
  };
  const updatePurchaseControls = (cart) => {
    const eligible = subscriptionLines(cart);
    const frequencyOptions = planNamesForCart(cart);
    if (!state.busy) {
      state.purchaseMode = eligible.length && eligible.every((line) => Boolean(line.selling_plan_allocation)) ? 'subscribe' : 'once';
      const activePlanName = eligible.find((line) => line.selling_plan_allocation)?.selling_plan_allocation?.selling_plan?.name || '';
      const activeFrequency = normalizePlanName(activePlanName);
      if (activeFrequency) state.purchaseFrequency = activeFrequency;
      if (!frequencyOptions.some((option) => option.value === state.purchaseFrequency)) state.purchaseFrequency = frequencyOptions[0]?.value || '';
      try {
        localStorage.setItem('outlaw_purchase_mode', state.purchaseMode);
        if (state.purchaseFrequency) localStorage.setItem('outlaw_purchase_frequency', state.purchaseFrequency);
      } catch (_) {}
    }
    $$('[data-purchase-controls]').forEach((controls) => {
      const subscribe = $('[data-purchase-mode][value="subscribe"]', controls);
      const once = $('[data-purchase-mode][value="once"]', controls);
      const subscribeLabel = $('[data-purchase-label="subscribe"]', controls);
      const onceLabel = $('[data-purchase-label="once"]', controls);
      const frequencyField = $('[data-frequency-field]', controls);
      const frequencySelect = $('[data-purchase-frequency]', controls);
      if (subscribe) { subscribe.disabled = state.busy || eligible.length === 0; subscribe.checked = state.purchaseMode === 'subscribe' && eligible.length > 0; }
      if (once) { once.disabled = state.busy; once.checked = state.purchaseMode !== 'subscribe' || eligible.length === 0; }
      subscribeLabel?.classList.toggle('chosen', Boolean(subscribe?.checked));
      onceLabel?.classList.toggle('chosen', Boolean(once?.checked));
      const subscribeHelp = $('[data-purchase-subscribe-help]', controls);
      if (subscribeHelp) subscribeHelp.textContent = eligible.length ? `Applies to ${eligible.length} eligible paid cart line${eligible.length === 1 ? '' : 's'}; free rewards stay one-time` : 'No subscription-eligible paid cans selected';
      if (frequencySelect) {
        frequencySelect.innerHTML = frequencyOptions.map((option) => `<option value="${escapeHtml(option.value)}">${escapeHtml(option.label)}</option>`).join('');
        if (state.purchaseFrequency) frequencySelect.value = state.purchaseFrequency;
        frequencySelect.disabled = state.busy;
      }
      if (frequencyField) frequencyField.hidden = !(state.purchaseMode === 'subscribe' && eligible.length > 0 && frequencyOptions.length > 0);
    });
  };
  const renderBuilderSummary = (cart) => {
    const count = paidCans(cart);
    const items = activeCartItems(cart);
    const paidItems = items.filter((line) => !rewardLine(line));
    const slots = [];
    paidItems.forEach((line) => {
      const units = Math.max(1, lineCanCount(line));
      for (let index = 0; index < units && slots.length < 6; index += 1) slots.push(line);
    });
    $$('[data-builder-slots]').forEach((root) => {
      let html = slots.map((line) => `<div class="filled">${line.image ? `<img src="${escapeHtml(line.image)}" alt="">` : ''}<small>${escapeHtml(line.product_title)}</small></div>`).join('');
      for (let index = slots.length; index < 6; index += 1) html += `<div><b>${String(index + 1).padStart(2, '0')}</b><small>OPEN</small></div>`;
      rewardLines(cart).sort((a, b) => rewardTier(a) - rewardTier(b)).forEach((reward) => {
        html += `<div class="reward-slot"><b aria-hidden="true">◆</b>${reward.image ? `<img src="${escapeHtml(reward.image)}" alt="">` : ''}<small>${rewardTier(reward) === 2 ? '2ND FREE' : '1ST FREE'} · ${escapeHtml(rewardDisplayName(reward))}</small></div>`;
      });
      root.innerHTML = html;
    });
    $$('[data-builder-lines]').forEach((root) => {
      root.hidden = !items.length;
      root.innerHTML = items.map((line) => {
        const meta = linePurchaseMeta(line);
        return `<div>${line.image ? `<img src="${escapeHtml(line.image)}" alt="">` : ''}<span><strong>${escapeHtml(line.product_title)}</strong><small>${escapeHtml(meta)}</small></span><em>${line.quantity}×</em><b>${rewardLine(line) && line.final_line_price === 0 ? 'FREE' : money(line.final_line_price)}</b></div>`;
      }).join('');
    });
    $$('[data-builder-form]').forEach((form) => {
      const variantId = $('[data-variant-select]', form)?.value || $('[name="id"]', form)?.value || '';
      const quantity = paidItems.filter((line) => String(line.variant_id) === String(variantId)).reduce((sum, line) => sum + Number(line.quantity || 0), 0);
      const card = form.closest('[data-builder-product]');
      const countNode = $('[data-builder-count]', form);
      const removeButton = $('[data-builder-remove]', form);
      if (countNode) countNode.textContent = quantity;
      if (removeButton) removeButton.disabled = state.busy || quantity === 0;
      card?.classList.toggle('selected', quantity > 0);
    });
    if (state.dialogSourceForm) {
      const variantId = $('[data-dialog-variant-select]')?.value || $('[data-variant-select]', state.dialogSourceForm)?.value || $('[name="id"]', state.dialogSourceForm)?.value || '';
      const quantity = paidItems.filter((line) => String(line.variant_id) === String(variantId)).reduce((sum, line) => sum + Number(line.quantity || 0), 0);
      const dialogCount = $('[data-dialog-count]'); if (dialogCount) dialogCount.textContent = quantity;
      const dialogRemove = $('[data-dialog-remove]'); if (dialogRemove) dialogRemove.disabled = state.busy || quantity === 0;
    }
    $$('[data-builder-reward-callout]').forEach((box) => {
      const tier = Number(box.dataset.builderRewardCallout || 1);
      const unlocked = rewardUnlocked(cart, tier); const selected = selectedReward(cart, tier); const remaining = Math.max(0, rewardThreshold(tier) - rewardEligibleCans(cart));
      const ordinal = tier === 2 ? 'SECOND' : 'FIRST';
      const lockedTitle = tier === 2 && rewardEligibleCans(cart) >= Number(settings.freeShippingAt || 10)
        ? `ONLY ${remaining} CAN${remaining === 1 ? '' : 'S'} AWAY FROM ANOTHER FREE CAN!`
        : `${ordinal} FREE CAN AT ${rewardThreshold(tier)} CANS`;
      box.classList.toggle('unlocked', unlocked);
      box.innerHTML = `<div><b aria-hidden="true">◆</b><span><strong>${unlocked ? selected ? `${ordinal} FREE CAN SELECTED` : `${ordinal} FREE CAN UNLOCKED` : lockedTitle}</strong><small>${unlocked ? selected ? `${escapeHtml(rewardDisplayName(selected))} is loaded as your ${ordinal.toLowerCase()} one-time reward.` : `Choose ${tier === 2 ? 'another' : 'any'} pictured eligible single can from Outlaw, Reloads or Berserker. The reward stays one-time.` : tier === 2 ? 'The second reward unlocks at 12 eligible paid single cans.' : `Add ${remaining} more to unlock your first free can. Eligible one-time and subscription single cans count.`}</small></span></div>${unlocked ? `<button type="button" data-reward-open data-reward-tier="${tier}">${selected ? `Change ${ordinal.toLowerCase()} free can` : `Choose ${ordinal.toLowerCase()} free can`}</button>` : ''}`;
    });
    $$('[data-builder-shipping-callout]').forEach((box) => {
      const unlocked = settings.freeShippingEnabled && count >= Number(settings.freeShippingAt || 10); const remaining = Math.max(0, Number(settings.freeShippingAt || 10) - count);
      box.classList.toggle('unlocked', unlocked);
      box.innerHTML = `<b aria-hidden="true">▰</b><span><strong>${unlocked ? 'FREE PRIORITY SHIPPING' : `${remaining} CANS TO FREE PRIORITY SHIPPING`}</strong><small>${unlocked ? 'Ten paid cans. Priority shipping is on us.' : 'Keep loading or check out when your stash is ready.'}</small></span>`;
    });
    $$('[data-stash-price-title]').forEach((node) => { node.textContent = count < Number(settings.minimumCans || 3) ? 'STASH TOTAL LOCKED' : 'STASH TOTAL'; });
    $$('[data-stash-price-value]').forEach((node) => { const remaining = Math.max(0, Number(settings.minimumCans || 3) - count); node.textContent = remaining ? `Add ${remaining} more can${remaining === 1 ? '' : 's'} to reveal pricing` : money(cart.total_price); });
    $$('.pack-summary [data-cart-open].pack-checkout').forEach((button) => button.classList.toggle('locked', count < Number(settings.minimumCans || 3)));
  };

  const renderCart = (cart) => {
    const lines = $('[data-cart-lines]'); if (!lines) return;
    const items = activeCartItems(cart); const count = paidCans(cart);
    if (!items.length) lines.innerHTML = '<div class="empty-cart"><strong class="display">0</strong><p>Your holster is empty.</p><a class="text-link" href="/collections/build-a-custom-6-pack">Build your stash →</a></div>';
    else lines.innerHTML = items.map((line) => {
      const isReward = rewardLine(line); const imageUrl = line.image ? `${line.image}${String(line.image).includes('?') ? '&' : '?'}width=180` : ''; const image = imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="">` : '';
      const meta = [line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title : '', isReward ? 'ONE-TIME REWARD' : line.selling_plan_allocation?.selling_plan?.name || ''].filter(Boolean).join(' · ');
      const price = isReward && line.final_line_price === 0 ? '<strong>FREE</strong>' : `<strong>${money(line.final_line_price)}</strong>`;
      const rewardLabel = isReward ? `<span class="reward-label">${rewardTier(line) === 2 ? '12-CAN · SECOND REWARD' : '6-CAN · FIRST REWARD'}</span>` : '';
      const rewardVariant = isReward && rewardVariantText(line) ? `<span class="reward-variant-badge">STRENGTH · ${escapeHtml(rewardVariantText(line))}</span>` : '';
      return `<article class="cart-line" data-line-key="${escapeHtml(line.key)}"><a class="cart-line-image" href="${escapeHtml(line.url)}">${image}</a><div><div class="cart-line-head"><a href="${escapeHtml(line.url)}">${escapeHtml(line.product_title)}</a>${price}</div>${rewardLabel}${rewardVariant}<p class="cart-line-meta">${escapeHtml(meta)}</p><div class="cart-line-controls"><div class="qty-control">${isReward ? '<span>1</span>' : `<button type="button" data-line-change="-1" data-key="${escapeHtml(line.key)}" aria-label="Remove one">−</button><span>${line.quantity}</span><button type="button" data-line-change="1" data-key="${escapeHtml(line.key)}" aria-label="Add one">+</button>`}</div><button type="button" class="cart-remove" data-line-remove data-key="${escapeHtml(line.key)}">Remove</button></div></div></article>`;
    }).join('');
    $$('[data-cart-reward]').forEach((rewardBox) => {
      const tier = Number(rewardBox.dataset.cartReward || 1); const reward = selectedReward(cart, tier); const ordinal = tier === 2 ? 'SECOND' : 'FIRST';
      rewardBox.hidden = !rewardUnlocked(cart, tier); rewardBox.classList.toggle('selected', Boolean(reward));
      const label = $('[data-reward-label]', rewardBox); const help = $('[data-reward-help]', rewardBox); const trigger = $('[data-reward-trigger-text]', rewardBox);
      if (label) label.textContent = reward ? `${ordinal} FREE CAN SELECTED` : `CHOOSE YOUR ${ordinal} FREE CAN`;
      if (help) help.textContent = reward ? `${rewardDisplayName(reward)} is loaded as your ${ordinal.toLowerCase()} one-time $0 reward.` : `Choose ${tier === 2 ? 'another' : 'any'} pictured eligible single can in the store.`;
      if (trigger) trigger.textContent = reward ? `Change ${ordinal.toLowerCase()} free can` : `Choose ${ordinal.toLowerCase()} free can`;
    });
    $$('[data-reward-open]').forEach((button) => { button.disabled = state.busy || !rewardUnlocked(cart, Number(button.dataset.rewardTier || 1)); });
    $$('[data-reward-option]').forEach((option) => {
      const reward = selectedReward(cart, state.rewardTier);
      const selected = Boolean(reward && String(option.dataset.variantIds || '').split(',').includes(String(reward.variant_id)));
      option.classList.toggle('selected', selected);
      $$('[data-reward-choose]', option).forEach((button) => { button.classList.toggle('active', Boolean(reward && String(button.dataset.variantId) === String(reward.variant_id))); button.disabled = state.busy || button.hasAttribute('data-unavailable'); });
    });
    const totals = $('[data-cart-totals]'); if (totals) totals.hidden = !items.length;
    const subtotal = $('[data-cart-subtotal]'); if (subtotal) subtotal.textContent = money(cart.total_price);
    const checkout = $('[data-checkout-button]');
    if (checkout) {
      const minimum = Number(settings.minimumCans || 3); const missingTier = nextRequiredRewardTier(cart);
      checkout.disabled = !canCheckout(cart) || state.busy;
      checkout.textContent = state.busy ? 'UPDATING…' : count < minimum ? `ADD ${minimum - count} MORE CAN${minimum - count === 1 ? '' : 'S'}` : missingTier ? `CHOOSE YOUR ${missingTier === 2 ? 'SECOND' : 'FIRST'} FREE CAN` : !rewardIsEligible(cart) ? 'RESELECT FREE-CAN REWARD' : 'CHECKOUT SECURELY →';
    }
    const visibleCart = optimisticCart(cart);
    updateProgress(visibleCart);
    renderBuilderSummary(visibleCart);
    updatePurchaseControls(cart);
  };
  const refreshCart = async () => {
    try {
      await getCart();
      const removedTiers = await pruneIneligibleRewards();
      if (removedTiers.length) {
        toast(`${removedTiers.length > 1 ? 'Free cans' : removedTiers[0] === 2 ? 'Second free can' : 'Free can'} removed because the required eligible paid single-can quantity is no longer in the stash.`);
        if ($('[data-cart-drawer]')?.hidden && refreshFullCartPage()) return;
      }
      renderCart(state.cart);
    } catch (error) { toast(errorText(error)); }
  };
  const setBusy = (busy) => { state.busy = busy; if (state.cart) renderCart(state.cart); };
  const changeLine = async (key, quantity) => {
    if (state.busy) return;
    setCartError(); setBusy(true);
    try {
      state.cart = await fetchJSON(routes.cartChange || '/cart/change.js', { method: 'POST', body: JSON.stringify({ id: key, quantity }) });
      state.cartPageDirty = true;
      const removedTiers = await pruneIneligibleRewards();
      if (removedTiers.length) toast(`${removedTiers.length > 1 ? 'Free cans' : removedTiers[0] === 2 ? 'Second free can' : 'Free can'} removed because the required eligible paid single-can quantity is no longer in the stash.`);
      renderCart(state.cart);
    }
    catch (error) { toast(errorText(error)); }
    finally { setBusy(false); }
  };
  const applyPurchaseMode = async (mode, frequency = '') => {
    if (state.busy) return;
    const requestedMode = mode === 'subscribe' ? 'subscribe' : 'once';
    state.purchaseMode = requestedMode;
    if (frequency) state.purchaseFrequency = normalizePlanName(frequency);
    setCartError(); setPurchaseStatus('Updating every eligible item in your stash…'); setBusy(true);
    try {
      await getCart();
      const eligible = subscriptionLines(state.cart);
      if (requestedMode === 'subscribe' && !eligible.length) throw new Error('Add a subscription-eligible can before choosing Subscribe & Save.');
      const names = planNamesForCart(state.cart);
      if (!state.purchaseFrequency || !names.some((option) => option.value === state.purchaseFrequency)) state.purchaseFrequency = names[0]?.value || '';
      const descriptors = eligible.map((line) => ({ key: line.key, variantId: line.variant_id, properties: JSON.stringify(line.properties || {}) }));
      for (const descriptor of descriptors) {
        const currentItems = activeCartItems(state.cart);
        const current = currentItems.find((line) => line.key === descriptor.key) || currentItems.find((line) => !rewardLine(line) && String(line.variant_id) === String(descriptor.variantId) && JSON.stringify(line.properties || {}) === descriptor.properties);
        if (!current) continue;
        const targetPlan = requestedMode === 'subscribe' ? planForVariant(current.variant_id, state.purchaseFrequency) : null;
        if (requestedMode === 'subscribe' && !targetPlan) continue;
        const currentPlanId = current.selling_plan_allocation?.selling_plan?.id || '';
        const targetPlanId = targetPlan?.id || '';
        if (String(currentPlanId) === String(targetPlanId)) continue;
        state.cart = await fetchJSON(routes.cartChange || '/cart/change.js', { method: 'POST', body: JSON.stringify({ id: current.key, quantity: current.quantity, selling_plan: targetPlanId ? Number(targetPlanId) : null }) });
        state.cartPageDirty = true;
      }
      const removedRewardTiers = await pruneIneligibleRewards();
      renderCart(state.cart);
      const message = requestedMode === 'subscribe' ? 'Subscribe & Save now applies to every eligible paid item. Free rewards stay one-time.' : 'Every eligible paid item is now a one-time purchase.';
      setPurchaseStatus(message); toast(removedRewardTiers.length ? `${message} An ineligible free-can reward was removed.` : message);
      window.setTimeout(() => setPurchaseStatus(), 4800);
      if ($('[data-cart-drawer]')?.hidden && refreshFullCartPage()) return;
    } catch (error) {
      await getCart().catch(() => null);
      if (state.cart) renderCart(state.cart);
      setPurchaseStatus(errorText(error), true); setCartError(errorText(error)); toast(errorText(error));
    } finally { setBusy(false); }
  };
  const addItem = async (id, quantity = 1, sellingPlan = '', properties = {}) => {
    const item = { id: Number(id), quantity, properties };
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    await fetchJSON(routes.cartAdd || '/cart/add.js', { method: 'POST', body: JSON.stringify({ items: [item] }) });
    state.cartPageDirty = true;
    await refreshCart();
  };
  const rewardVerificationDelays = Array.isArray(cfg.rewardVerificationDelays) ? cfg.rewardVerificationDelays : [0, 300, 700, 1200, 1800];
  const verifiedRewardLine = (variantId, tier) => {
    const line = selectedReward(state.cart, tier);
    const everyRewardIsFree = rewardLines(state.cart).every((reward) => Number(reward.final_line_price) === 0);
    return line && String(line.variant_id) === String(variantId) && Number(line.final_line_price) === 0 && everyRewardIsFree ? line : null;
  };
  const waitForRewardDiscount = async (variantId, tier) => {
    for (const delay of rewardVerificationDelays) {
      if (delay > 0) await wait(delay);
      if (delay > 0) await getCart();
      const verified = verifiedRewardLine(variantId, tier);
      if (verified) return verified;
    }

    // A no-op line update forces Shopify to recalculate automatic discounts.
    // This covers slower mobile sessions where /cart/add returns before the
    // automatic free-can discount is reflected by /cart.js.
    const pending = selectedReward(state.cart, tier);
    if (pending && String(pending.variant_id) === String(variantId)) {
      state.cart = await fetchJSON(routes.cartChange || '/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: pending.key, quantity: 1 })
      });
      state.cartPageDirty = true;
      if (verifiedRewardLine(variantId, tier)) return selectedReward(state.cart, tier);
      await wait(500);
      await getCart();
    }
    return verifiedRewardLine(variantId, tier);
  };
  const removeRewardTier = async (tier) => {
    const cart = state.cart || await getCart(); const updates = {};
    rewardLines(cart, tier).forEach((line) => { updates[line.key] = 0; });
    if (Object.keys(updates).length) {
      state.cart = await fetchJSON(routes.cartUpdate || '/cart/update.js', { method: 'POST', body: JSON.stringify({ updates }) });
      state.cartPageDirty = true;
    }
  };
  const pruneIneligibleRewards = async () => {
    const cart = state.cart || await getCart();
    const invalidTiers = [1, 2].filter((tier) => {
      const lines = rewardLines(cart, tier);
      return lines.length && (!rewardUnlocked(cart, tier) || lines.length > 1 || lines.some((line) => !rewardVariantIds.has(String(line.variant_id)) || Number(line.quantity) !== 1));
    });
    if (!invalidTiers.length) return [];
    const updates = {};
    rewardLines(cart).forEach((line) => { if (invalidTiers.includes(rewardTier(line))) updates[line.key] = 0; });
    state.cart = await fetchJSON(routes.cartUpdate || '/cart/update.js', { method: 'POST', body: JSON.stringify({ updates }) });
    state.cartPageDirty = true;
    try { invalidTiers.forEach((tier) => localStorage.removeItem(`outlaw_reward_variant_${tier}`)); } catch (_) {}
    return invalidTiers;
  };
  const chooseReward = async (variantId, tier = state.rewardTier) => {
    if (state.busy) return false;
    tier = Number(tier) === 2 ? 2 : 1;
    setCartError(); setBusy(true); let succeeded = false; let previousRewardItem = null;
    try {
      await getCart();
      await pruneIneligibleRewards();
      if (variantId && !rewardUnlocked(state.cart, tier)) throw new Error(`Choose your ${tier === 2 ? 'second' : 'first'} free can after adding ${rewardThreshold(tier)} eligible paid single cans. One-time and subscription single cans count.`);
      if (variantId && !rewardVariantIds.has(String(variantId))) throw new Error('That can is not included in the current free-can promotion. Choose one of the pictured eligible single cans.');
      const previousReward = selectedReward(state.cart, tier);
      if (variantId && previousReward && String(previousReward.variant_id) === String(variantId) && Number(previousReward.final_line_price) === 0) {
        toast(`${tier === 2 ? 'Second f' : 'F'}ree can is already loaded.`); return true;
      }
      if (previousReward) previousRewardItem = cartRestoreItem(previousReward);
      await removeRewardTier(tier);
      if (variantId) {
        await fetchJSON(routes.cartAdd || '/cart/add.js', {
          method: 'POST',
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: { _outlaw_reward: 'true', _outlaw_reward_tier: String(tier), _can_count: '0' } }] })
        });
        state.cartPageDirty = true;
        await getCart();
        const line = settings.rewardDiscountCode ? selectedReward(state.cart, tier) : await waitForRewardDiscount(variantId, tier);
        if (!settings.rewardDiscountCode && !line) {
          await removeRewardTier(tier); await refreshCart();
          throw new Error(`The ${tier === 2 ? 'second ' : ''}free-can promotion is not applying yet. Your selection was removed so it cannot be charged. Please contact Outlaw support before checkout.`);
        }
        renderCart(state.cart);
        try { localStorage.setItem(`outlaw_reward_variant_${tier}`, String(variantId)); } catch (_) {}
        toast(`${tier === 2 ? 'Second f' : 'F'}ree can loaded. ${progressMessage(state.cart)}.`);
        succeeded = true;
      } else {
        try { localStorage.removeItem(`outlaw_reward_variant_${tier}`); } catch (_) {}
        await refreshCart();
        succeeded = true;
      }
    } catch (error) {
      if (previousRewardItem && !selectedReward(state.cart, tier) && rewardUnlocked(state.cart, tier)) {
        try {
          await fetchJSON(routes.cartAdd || '/cart/add.js', { method: 'POST', body: JSON.stringify({ items: [previousRewardItem] }) });
          state.cartPageDirty = true; await getCart(); renderCart(state.cart);
        } catch (_) {}
      }
      setCartError(errorText(error)); toast(errorText(error));
    }
    finally { setBusy(false); }
    return succeeded;
  };
  const closeRewardDialog = () => {
    const dialog = $('[data-reward-dialog]'); if (!dialog) return;
    const wasOpen = dialog.open || dialog.hasAttribute('open');
    if (dialog.open && typeof dialog.close === 'function') dialog.close(); else dialog.removeAttribute('open');
    setOverlayOpen('reward-dialog-open', false);
    if (wasOpen && rewardReturnFocus?.isConnected) rewardReturnFocus.focus({ preventScroll: true });
    rewardReturnFocus = null;
  };
  const openRewardDialog = async (trigger = null, requestedTier = 0) => {
    const dialog = $('[data-reward-dialog]'); if (!dialog) return;
    if (state.pendingAdds.some((pending) => pending.status === 'queued')) flushBuilderAdds();
    await state.addQueue.catch(() => null);
    if (!state.cart) await getCart();
    const triggerTier = Number(trigger?.dataset?.rewardTier || 0);
    const tier = Number(requestedTier) === 2 || triggerTier === 2 ? 2 : Number(requestedTier) === 1 || triggerTier === 1 ? 1 : nextRequiredRewardTier(state.cart) || 1;
    if (!rewardUnlocked(state.cart, tier)) return toast(`Add ${rewardThreshold(tier)} eligible paid single cans to unlock your ${tier === 2 ? 'second' : 'first'} free can.`);
    state.rewardTier = tier;
    rewardReturnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement instanceof HTMLElement ? document.activeElement : null;
    renderCart(state.cart);
    dialog.dataset.rewardTier = String(tier);
    const eyebrow = $('[data-reward-dialog-eyebrow]', dialog); if (eyebrow) eyebrow.textContent = `${tier === 2 ? 'Twelve' : 'Six'}-can reward · One-time item`;
    const title = $('[data-reward-dialog-title]', dialog); if (title) title.innerHTML = `PICK YOUR<br><em>${tier === 2 ? 'SECOND' : 'FIRST'} FREE CAN.</em>`;
    const status = $('[data-reward-dialog-status]', dialog); if (status) status.textContent = selectedReward(state.cart, tier) ? `Choose another pictured can to replace your ${tier === 2 ? 'second' : 'first'} one-time reward.` : `Your selected can will appear in the stash as your ${tier === 2 ? 'second' : 'first'} one-time $0 reward.`;
    if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
    setOverlayOpen('reward-dialog-open', true);
    window.requestAnimationFrame(() => $('[data-reward-close]', dialog)?.focus({ preventScroll: true }));
  };
  const initRewardDialog = () => {
    const dialog = $('[data-reward-dialog]'); if (!dialog) return;
    $$('[data-reward-close]', dialog).forEach((button) => button.addEventListener('click', closeRewardDialog));
    dialog.addEventListener('click', (event) => { if (event.target === dialog) closeRewardDialog(); });
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); closeRewardDialog(); });
    dialog.addEventListener('close', () => { setOverlayOpen('reward-dialog-open', false); });
    dialog.addEventListener('keydown', (event) => trapFocus(event, dialog));
    $$('[data-reward-filter]', dialog).forEach((filter) => filter.addEventListener('click', () => {
      const category = filter.dataset.rewardFilter || 'all';
      $$('[data-reward-filter]', dialog).forEach((button) => { const active = button === filter; button.classList.toggle('active', active); button.setAttribute('aria-pressed', String(active)); });
      $$('[data-reward-option]', dialog).forEach((option) => { option.hidden = category !== 'all' && option.dataset.category !== category; });
    }));
    $$('[data-reward-options-trigger]', dialog).forEach((button) => button.addEventListener('click', () => {
      const option = button.closest('[data-reward-option]');
      const firstChoice = $('[data-reward-choose]:not([disabled])', option);
      option?.classList.add('awaiting-choice');
      const status = $('[data-reward-dialog-status]', dialog);
      if (status) status.textContent = 'Choose a strength below to add this free can.';
      firstChoice?.focus({ preventScroll: true });
    }));
    $$('[data-reward-choose]', dialog).forEach((button) => button.addEventListener('click', async () => {
      const status = $('[data-reward-dialog-status]', dialog);
      $$('[data-reward-choose]', button.closest('[data-reward-option]')).forEach((choice) => choice.classList.toggle('pending', choice === button));
      if (status) status.textContent = `Selected ${String(button.textContent || '').trim() || 'option'}. Verifying your $0 reward…`;
      const completedTier = state.rewardTier;
      const succeeded = await chooseReward(button.dataset.variantId || '', completedTier);
      if (succeeded) {
        closeRewardDialog();
        const nextTier = nextRequiredRewardTier(state.cart);
        if (nextTier && nextTier !== completedTier) window.setTimeout(() => openRewardDialog($(`[data-reward-open][data-reward-tier="${nextTier}"]`), nextTier), 80);
      }
      else if (status) status.textContent = 'That selection could not be loaded. Review the message in your stash and try again.';
    }));
  };
  const openCart = async () => {
    const drawer = $('[data-cart-drawer]'); if (!drawer) return;
    if (drawer.hidden && document.activeElement instanceof HTMLElement) cartReturnFocus = document.activeElement;
    drawer.hidden = false; drawer.setAttribute('aria-hidden', 'false'); setOverlayOpen('cart-open', true);
    await refreshCart();
    $('.cart-drawer-panel [data-cart-close]', drawer)?.focus({ preventScroll: true });
  };
  const closeCart = (restoreFocus = true) => {
    const drawer = $('[data-cart-drawer]'); if (!drawer) return;
    const wasOpen = !drawer.hidden;
    drawer.hidden = true; drawer.setAttribute('aria-hidden', 'true'); setOverlayOpen('cart-open', false); refreshFullCartPage();
    if (restoreFocus && wasOpen && cartReturnFocus?.isConnected) cartReturnFocus.focus({ preventScroll: true });
    if (wasOpen) cartReturnFocus = null;
  };
  const checkout = async () => {
    if (state.busy) return;
    setCartError(); setBusy(true);
    try {
      const sanitation = await sanitizeCartForCheckout();
      if (sanitation.cleaned) renderCart(sanitation.cart);
      const removedRewardTiers = await pruneIneligibleRewards();
      if (removedRewardTiers.length) renderCart(state.cart);
      const cart = state.cart; const count = paidCans(cart); const rewards = rewardLines(cart);
      if (count < Number(settings.minimumCans || 3)) return toast(`Add ${Number(settings.minimumCans || 3) - count} more cans to unlock checkout.`);
      const missingTier = nextRequiredRewardTier(cart);
      if (missingTier) { openRewardDialog($('[data-checkout-button]'), missingTier); return toast(`Choose your ${missingTier === 2 ? 'second' : 'first'} free can first.`); }
      if (!rewardIsEligible(cart)) throw new Error('A free-can reward is not at $0 or is no longer eligible. Please reselect the affected reward before checkout.');
      const destination = routes.checkout || '/checkout';
      window.location.href = settings.rewardDiscountCode && rewards.length ? `/discount/${encodeURIComponent(settings.rewardDiscountCode)}?redirect=${encodeURIComponent(destination)}` : destination;
    } catch (error) { setCartError(errorText(error)); toast(errorText(error)); }
    finally { setBusy(false); }
  };

  const initMenu = () => {
    const menu = $('[data-mobile-menu]');
    if (!menu) return;
    $$('[data-menu-open]').forEach((button) => button.addEventListener('click', () => {
      menuReturnFocus = button; menu.hidden = false; button.setAttribute('aria-expanded', 'true'); setOverlayOpen('menu-open', true);
      window.requestAnimationFrame(() => $('.mobile-menu-head [data-menu-close]', menu)?.focus({ preventScroll: true }));
    }));
    $$('[data-menu-close]', menu).forEach((button) => button.addEventListener('click', () => {
      menu.hidden = true; $('[data-menu-open]')?.setAttribute('aria-expanded', 'false'); setOverlayOpen('menu-open', false);
      if (menuReturnFocus?.isConnected) menuReturnFocus.focus({ preventScroll: true });
      menuReturnFocus = null;
    }));
    menu.addEventListener('keydown', (event) => trapFocus(event, menu));
  };
  const initCartDrawer = () => {
    const drawer = $('[data-cart-drawer]');
    if (drawer) drawer.addEventListener('keydown', (event) => trapFocus(event, drawer));
  };
  const formVariantId = (form) => $('[data-variant-select]', form)?.value || $('[name="id"]', form)?.value || '';
  const formVariantData = (form) => $('[data-variant-select]', form)?.selectedOptions?.[0] || form;
  const renderPendingCart = () => {
    const cart = optimisticCart();
    updateProgress(cart);
    renderBuilderSummary(cart);
  };
  const flushBuilderAdds = () => {
    window.clearTimeout(state.addFlushTimer); state.addFlushTimer = null;
    const batch = state.pendingAdds.filter((pending) => pending.status === 'queued');
    if (!batch.length) return state.addQueue;
    batch.forEach((pending) => { pending.status = 'sending'; });
    state.addQueue = state.addQueue.then(async () => {
      const wasUnlocked = [false, rewardUnlocked(state.cart || { items: [] }, 1), rewardUnlocked(state.cart || { items: [] }, 2)];
      const grouped = new Map();
      batch.forEach((pending) => {
        const key = `${pending.variantId}|${pending.sellingPlan || ''}|${JSON.stringify(pending.properties || {})}`;
        if (!grouped.has(key)) grouped.set(key, { id: Number(pending.variantId), quantity: 0, properties: pending.properties || {}, ...(pending.sellingPlan ? { selling_plan: Number(pending.sellingPlan) } : {}) });
        grouped.get(key).quantity += Number(pending.quantity || 0);
      });
      try {
        await fetchJSON(routes.cartAdd || '/cart/add.js', { method: 'POST', body: JSON.stringify({ items: Array.from(grouped.values()) }) });
        state.cartPageDirty = true;
        state.pendingAdds = state.pendingAdds.filter((pending) => !batch.some((sent) => sent.token === pending.token));
        await getCart();
        batch.forEach((pending) => { const message = $('[data-product-message]', pending.form); if (message) message.textContent = 'Added to your loadout.'; });
        renderCart(state.cart);
        toast(batch.reduce((sum, pending) => sum + Number(pending.quantity || 0), 0) > 1 ? 'Cans added to your loadout.' : 'Added to your loadout.');
        const newlyUnlockedTier = [1, 2].find((tier) => !wasUnlocked[tier] && rewardUnlocked(state.cart, tier) && !selectedReward(state.cart, tier));
        if (newlyUnlockedTier) window.setTimeout(() => openRewardDialog(batch[batch.length - 1]?.trigger || null, newlyUnlockedTier), 80);
      } catch (error) {
        state.pendingAdds = state.pendingAdds.filter((pending) => !batch.some((failed) => failed.token === pending.token));
        await getCart().catch(() => null);
        if (state.cart) renderCart(state.cart);
        batch.forEach((pending) => { const message = $('[data-product-message]', pending.form); if (message) message.textContent = errorText(error); });
        toast(errorText(error));
      }
    });
    state.addQueue = state.addQueue.catch(() => null);
    return state.addQueue;
  };
  const queueBuilderAdd = (form, selectedPlan, properties, quantity, trigger) => {
    const data = formVariantData(form); const card = form.closest('[data-builder-product]');
    const record = {
      token: ++state.nextPendingAdd,
      status: 'queued',
      variantId: formVariantId(form),
      productId: card?.dataset.productId || '',
      productTitle: card?.dataset.productTitle || 'Outlaw single can',
      variantTitle: data === form ? 'Single can' : String(data?.textContent || 'Single can').trim().replace(/\s+—\s+Sold out$/i, ''),
      image: data?.dataset.image || $('.pack-card-visual img', card)?.currentSrc || $('.pack-card-visual img', card)?.src || '',
      url: card?.dataset.productUrl || '#',
      priceCents: Number(data?.dataset.priceCents || form.dataset.priceCents || 0),
      sellingPlan: selectedPlan?.id || '',
      sellingPlanName: selectedPlan?.name || '',
      properties,
      quantity,
      form,
      trigger
    };
    state.pendingAdds.push(record);
    const message = $('[data-product-message]', form); if (message) message.textContent = 'Adding…';
    renderPendingCart();
    window.clearTimeout(state.addFlushTimer);
    state.addFlushTimer = window.setTimeout(flushBuilderAdds, 70);
  };
  const removeBuilderItem = async (form) => {
    if (!form || state.busy) return;
    const variantId = formVariantId(form);
    const pending = [...state.pendingAdds].reverse().find((item) => String(item.variantId) === String(variantId));
    if (pending?.status === 'queued') {
      pending.quantity -= 1;
      if (pending.quantity <= 0) state.pendingAdds = state.pendingAdds.filter((item) => item.token !== pending.token);
      renderPendingCart();
      return;
    }
    if (pending?.status === 'sending') await state.addQueue;
    if (!state.cart) await getCart();
    const matches = activeCartItems(state.cart).filter((line) => !rewardLine(line) && String(line.variant_id) === String(variantId));
    const preferred = matches.find((line) => Boolean(line.selling_plan_allocation) === (state.purchaseMode === 'subscribe')) || matches[0];
    if (preferred) await changeLine(preferred.key, Math.max(0, preferred.quantity - 1));
  };
  const initProductForms = (root = document) => {
    $$('[data-product-form]', root).forEach((form) => {
      if (form.dataset.outlawInitialized === 'true') return;
      form.dataset.outlawInitialized = 'true';
      const variant = $('[data-variant-select]', form);
      const fixedCanCount = form.dataset.canCount || '';
      const visualRoot = form.closest('[data-product-root]') || form.closest('.pack-card') || form.closest('.product-card') || form;
      const updateVariant = () => {
        const option = variant?.selectedOptions?.[0];
        const inferredCanCount = canCountFromLabel(option?.textContent || '');
        if (inferredCanCount) form.dataset.canCount = String(inferredCanCount);
        else if (fixedCanCount) form.dataset.canCount = fixedCanCount;
        else delete form.dataset.canCount;
        setProductImage($('[data-product-main-image], .product-visual img, .pack-card-visual img', visualRoot), option?.dataset.image, option?.dataset.imageAlt);
        if (state.cart) renderBuilderSummary(optimisticCart(state.cart));
      };
      variant?.addEventListener('change', updateVariant);
      updateVariant();
      form.addEventListener('submit', async (event) => {
        event.preventDefault(); if (state.busy) return;
        const id = formVariantId(form); const message = $('[data-product-message]', form); const builderForm = form.hasAttribute('data-builder-form');
        setCartError(); if (message) message.textContent = '';
        try {
          const selectedPlan = state.purchaseMode === 'subscribe' ? planForVariant(id, state.purchaseFrequency) : null;
          const properties = form.dataset.canCount ? { _can_count: form.dataset.canCount } : {};
          const quantity = Number($('[name="quantity"]', form)?.value || 1);
          if (builderForm) {
            queueBuilderAdd(form, selectedPlan, properties, quantity, event.submitter || null);
            return;
          }
          if (!state.cart) { await getCart(); renderCart(state.cart); }
          setBusy(true);
          await addItem(id, quantity, selectedPlan?.id || '', properties);
          if (message) message.textContent = 'Added to your loadout.';
          if (state.dialogSourceForm === form) { const dialogMessage = $('[data-dialog-message]'); if (dialogMessage) dialogMessage.textContent = 'Added to your loadout.'; }
          toast('Added to your loadout.');
          if (!builderForm) await openCart();
        } catch (error) {
          if (message) message.textContent = errorText(error);
          if (state.dialogSourceForm === form) { const dialogMessage = $('[data-dialog-message]'); if (dialogMessage) dialogMessage.textContent = errorText(error); }
          toast(errorText(error));
        } finally { setBusy(false); }
      });
    });
  };
  const closeProductDialog = () => {
    const dialog = $('[data-product-dialog]'); if (!dialog) return;
    if (dialog.open) dialog.close(); else dialog.removeAttribute('open');
    setOverlayOpen('product-dialog-open', false);
    state.dialogSourceForm = null;
  };
  const renderDialogReviews = (dialog, card) => {
    const productId = String(card.dataset.productId || '');
    const productTitle = card.dataset.productTitle || '';
    const productUrl = card.dataset.productUrl || '#';
    const product = $('[data-dialog-review-product]', dialog);
    const link = $('[data-dialog-reviews-link]', dialog);
    const host = $('[data-dialog-review-widget]', dialog);
    if (product) product.textContent = productTitle;
    if (link) link.href = `${productUrl}#reviews`;
    if (!host || !productId) return;
    host.dataset.reviewProductId = productId;
    const widget = document.createElement('div');
    widget.className = 'yotpo-widget-instance';
    widget.dataset.yotpoInstanceId = '421371';
    widget.dataset.yotpoProductId = productId;
    widget.dataset.yotpoName = productTitle;
    try { widget.dataset.yotpoUrl = new URL(productUrl, window.location.href).toString(); } catch (_) { widget.dataset.yotpoUrl = productUrl; }
    host.replaceChildren(widget);
    let attempts = 0;
    const initialize = () => {
      if (host.dataset.reviewProductId !== productId) return;
      try {
        if (typeof window.yotpoWidgetsContainer?.initWidgets === 'function') { window.yotpoWidgetsContainer.initWidgets(); return; }
        if (typeof window.yotpo?.initWidgets === 'function') { window.yotpo.initWidgets(); return; }
      } catch (_) {}
      attempts += 1;
      if (attempts < 30) window.setTimeout(initialize, 150);
    };
    window.setTimeout(initialize, 0);
  };
  const openProductDialog = (trigger) => {
    const card = trigger?.closest('[data-builder-product]'); const dialog = $('[data-product-dialog]'); if (!card || !dialog) return;
    const form = $('[data-builder-form]', card); if (!form) return;
    state.dialogSourceForm = form;
    const sourceSelect = $('[data-variant-select]', form); const dialogSelect = $('[data-dialog-variant-select]', dialog); const field = $('[data-dialog-variant-field]', dialog);
    const selectedOption = sourceSelect?.selectedOptions?.[0]; const cardImage = $('.pack-card-visual img', card);
    const image = $('[data-dialog-image]', dialog); const category = card.dataset.productCategory || '';
    dialog.style.setProperty('--pack-accent', card.style.getPropertyValue('--pack-accent') || '#ffd21f');
    $('[data-product-dialog-grid]', dialog)?.style.setProperty('--pack-accent', card.style.getPropertyValue('--pack-accent') || '#ffd21f');
    $('[data-dialog-title]', dialog).textContent = card.dataset.productTitle || '';
    $('[data-dialog-description]', dialog).textContent = card.dataset.productDescription || '';
    $('[data-dialog-category]', dialog).textContent = category;
    $('[data-dialog-category-copy]', dialog).textContent = `${category} · PRODUCT DETAILS`;
    const fullLink = $('[data-dialog-full-link]', dialog); fullLink.href = card.dataset.productUrl || '#';
    renderDialogReviews(dialog, card);
    if (sourceSelect && dialogSelect) {
      dialogSelect.innerHTML = sourceSelect.innerHTML;
      dialogSelect.value = sourceSelect.value;
      field.hidden = false;
      $('[data-dialog-variant-label]', dialog).textContent = card.dataset.category === 'reloads' ? 'Choose your strength' : 'Choose your option';
    } else if (field) { field.hidden = true; if (dialogSelect) dialogSelect.innerHTML = ''; }
    setProductImage(image, selectedOption?.dataset.image || cardImage?.currentSrc || cardImage?.src, selectedOption?.dataset.imageAlt || card.dataset.productTitle || '');
    const dialogMessage = $('[data-dialog-message]', dialog); if (dialogMessage) dialogMessage.textContent = '';
    if (state.cart) renderBuilderSummary(state.cart);
    if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
    setOverlayOpen('product-dialog-open', true);
  };
  const initProductDialog = () => {
    const dialog = $('[data-product-dialog]'); if (!dialog) return;
    $('[data-product-dialog-close]', dialog)?.addEventListener('click', closeProductDialog);
    dialog.addEventListener('click', (event) => { if (event.target === dialog) closeProductDialog(); });
    dialog.addEventListener('close', () => { setOverlayOpen('product-dialog-open', false); state.dialogSourceForm = null; });
    $('[data-dialog-variant-select]', dialog)?.addEventListener('change', (event) => {
      const sourceSelect = state.dialogSourceForm ? $('[data-variant-select]', state.dialogSourceForm) : null;
      if (sourceSelect) { sourceSelect.value = event.target.value; sourceSelect.dispatchEvent(new Event('change', { bubbles: true })); }
      const option = event.target.selectedOptions[0]; setProductImage($('[data-dialog-image]', dialog), option?.dataset.image, option?.dataset.imageAlt);
      if (state.cart) renderBuilderSummary(state.cart);
    });
    $('[data-dialog-add]', dialog)?.addEventListener('click', () => state.dialogSourceForm?.requestSubmit());
    $('[data-dialog-remove]', dialog)?.addEventListener('click', () => removeBuilderItem(state.dialogSourceForm));
  };
  const initBuilder = () => {
    let activeCategory = 'all';
    const search = $('[data-builder-search]');
    const applyFilters = () => {
      const query = String(search?.value || '').trim().toLowerCase(); let visible = 0;
      $$('[data-builder-product]').forEach((card) => {
        const categoryMatch = activeCategory === 'all' || card.dataset.category === activeCategory;
        const queryMatch = !query || String(card.textContent || '').toLowerCase().includes(query);
        card.hidden = !(categoryMatch && queryMatch);
        if (!card.hidden) visible += 1;
      });
      const empty = $('[data-builder-empty]'); if (empty) empty.hidden = visible > 0;
    };
    $$('[data-builder-tab]').forEach((tab) => tab.addEventListener('click', () => {
      activeCategory = tab.dataset.builderTab || 'all';
      $$('[data-builder-tab]').forEach((node) => { node.classList.toggle('active', node === tab); node.setAttribute('aria-pressed', String(node === tab)); });
      applyFilters();
    }));
    search?.addEventListener('input', applyFilters);
  };
  const initGallery = () => $$('[data-product-thumb]').forEach((button) => button.addEventListener('click', () => {
    setProductImage($('[data-product-main-image]'), button.dataset.image, button.dataset.alt);
    $$('[data-product-thumb]').forEach((item) => {
      const selected = item === button;
      item.classList.toggle('active', selected);
      item.setAttribute('aria-pressed', String(selected));
    });
  }));
  const initLiveReviewCount = () => {
    const badge = $('.yotpo-rating-badge');
    const section = $('.yotpo-review-section');
    if (!badge || !section) return;
    const sync = () => {
      const label = $('.yotpo-bottom-line-basic-text', section);
      if (!label) return;
      const match = String(badge.textContent || '').match(/([\d,]+)\s+reviews?\b/i);
      const count = match ? Number.parseInt(match[1].replace(/,/g, ''), 10) : NaN;
      const copy = Number.isFinite(count) ? `Based on ${count.toLocaleString('en-US')} review${count === 1 ? '' : 's'}` : 'Verified customer reviews';
      if (label.textContent.trim() !== copy) label.textContent = copy;
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(badge, { childList: true, subtree: true, characterData: true });
    observer.observe(section, { childList: true, subtree: true, characterData: true });
    window.setTimeout(() => { sync(); observer.disconnect(); }, 20000);
  };
  const initHomeHero = () => {
    const root = $('[data-home-hero]'); if (!root) return;
    if (root.dataset.heroInitialized === 'true') return;
    root.dataset.heroInitialized = 'true';
    const options = $$('[data-hero-option]', root); if (!options.length) return;
    const image = $('[data-hero-image]', root);
    const placeholder = $('[data-hero-placeholder]', root);
    const links = $$('[data-hero-link],[data-hero-action],[data-hero-mobile-link]', root);
    const name = $('[data-hero-name]', root); const mobileName = $('[data-hero-mobile-name]', root);
    const category = $('[data-hero-category]', root); const count = $('[data-hero-count]', root); const mobileCount = $('[data-hero-mobile-count]', root);
    const totalLabel = String(options.length).padStart(2, '0');
    let active = 0; let timer = null; let heroRequest = 0;
    const applyHero = (nextIndex, option, label) => {
      active = nextIndex;
      root.style.setProperty('--hero-accent', option.dataset.accent || '#ffd21f');
      options.forEach((item, itemIndex) => item.setAttribute('aria-pressed', String(itemIndex === active)));
      links.forEach((link) => {
        if (option.dataset.url) link.href = option.dataset.url;
        link.setAttribute('aria-label', 'View ' + label);
      });
      const hasImage = option.dataset.hasImage === 'true' && Boolean(option.dataset.image);
      if (image) {
        image.hidden = !hasImage;
        if (hasImage) image.src = option.dataset.image;
        image.alt = option.dataset.alt || label;
        image.classList.remove('is-changing');
        if (hasImage) window.requestAnimationFrame(() => image.classList.add('is-changing'));
      }
      if (placeholder) placeholder.hidden = hasImage;
      if (name) name.textContent = label;
      if (mobileName) mobileName.textContent = label;
      if (category) category.textContent = option.dataset.category || '';
      const activeLabel = String(active + 1).padStart(2, '0');
      if (count) count.textContent = activeLabel + ' / ' + totalLabel;
      if (mobileCount) mobileCount.textContent = 'Featured · ' + activeLabel + '/' + totalLabel;
    };
    const show = (index) => {
      const nextIndex = (index + options.length) % options.length;
      const option = options[nextIndex]; const label = option.dataset.name || '';
      const source = option.dataset.image || '';
      const currentSource = image ? (image.currentSrc || image.src || '') : '';
      const request = ++heroRequest;
      if (!image || option.dataset.hasImage !== 'true' || !source || currentSource === source) { applyHero(nextIndex, option, label); return; }
      const preloader = new Image(); let committed = false;
      const commit = () => {
        if (committed || request !== heroRequest) return;
        committed = true;
        applyHero(nextIndex, option, label);
      };
      preloader.addEventListener('load', commit, { once: true });
      preloader.src = source;
      if (preloader.complete) {
        if (preloader.decode) preloader.decode().then(commit).catch(commit);
        else commit();
      }
    };
    const start = () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      window.clearInterval(timer);
      timer = window.setInterval(() => { if (!document.hidden) show(active + 1); }, 3000);
    };
    options.forEach((option, index) => {
      option.addEventListener('click', () => { show(index); start(); });
      option.addEventListener('keydown', (event) => {
        const keyTargets = { ArrowUp: index - 1, ArrowLeft: index - 1, ArrowDown: index + 1, ArrowRight: index + 1, Home: 0, End: options.length - 1 };
        if (!(event.key in keyTargets)) return;
        event.preventDefault();
        const nextIndex = (keyTargets[event.key] + options.length) % options.length;
        options[nextIndex].focus(); show(nextIndex); start();
      });
      if (option.dataset.hasImage === 'true' && option.dataset.image && (!image || option.dataset.image !== (image.currentSrc || image.src))) {
        const preload = new Image(); preload.src = option.dataset.image;
      }
    });
    root.outlawHeroStop = () => window.clearInterval(timer);
    show(0); start();
  };
  const initBrandMarquee = () => {
    const button = $('[data-marquee-toggle]'); const track = $('[data-marquee-track]'); if (!button || !track) return;
    button.addEventListener('click', () => {
      const paused = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', String(paused));
      button.setAttribute('aria-label', paused ? 'Play brand ticker' : 'Pause brand ticker');
      track.style.animationPlayState = paused ? 'paused' : 'running';
      const icon = $('[data-marquee-icon]', button); if (icon) icon.textContent = paused ? '▶' : 'Ⅱ';
    });
  };
  const initFeaturedReviews = () => {
    const root = $('[data-featured-reviews]'); if (!root) return;
    const items = $$('[data-review-item]', root); const previous = $('[data-review-previous]', root); const next = $('[data-review-next]', root); const status = $('[data-review-status]', root);
    if (!items.length || !previous || !next) return;
    const pages = Math.max(...items.map((item) => Number(item.dataset.reviewPage || 0))) + 1;
    let page = 0;
    const render = () => {
      items.forEach((item) => { item.hidden = Number(item.dataset.reviewPage || 0) !== page; });
      previous.disabled = page === 0; next.disabled = page === pages - 1;
      if (status) status.textContent = (page * 3 + 1) + '–' + Math.min((page + 1) * 3, items.length) + ' of ' + items.length + ' highlights';
      $('#FeaturedReviewCards', root)?.scrollTo({ left: 0, behavior: 'smooth' });
    };
    previous.addEventListener('click', () => { page = Math.max(0, page - 1); render(); });
    next.addEventListener('click', () => { page = Math.min(pages - 1, page + 1); render(); });
    render();
  };

  /* Pure helpers exposed for the automated theme regression suite. */
  window.OutlawThemeTests = { activeCartItems, staleCartItems, cartRestoreItem, sameActiveCartContents, sanitizeCartForCheckout, lineCanCount, lineFormatLabel, linePurchaseMeta, rewardVariantText, rewardDisplayName, canCountFromLabel, paidCans, rewardEligibleCans, rewardLine, rewardTier, rewardLines, rewardThreshold, selectedReward, rewardUnlocked, rewardRequired, rewardIsEligible, nextRequiredRewardTier, canCheckout, progressMessage, progressPercent, selectedPrice, setProductImage, normalizePlanName, planForVariant, optimisticCart, getCart, renderBuilderSummary, updatePurchaseControls, renderCart, removeRewardTier, pruneIneligibleRewards, chooseReward, applyPurchaseMode, initProductForms, openProductDialog, closeProductDialog, openRewardDialog, closeRewardDialog };

  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-cart-open],[data-cart-close],[data-reward-open],[data-line-change],[data-line-remove],[data-checkout-button],[data-builder-remove],[data-product-quick-view]'); if (!target) return;
    if (target.matches('[data-cart-open]')) { event.preventDefault(); openCart(); }
    else if (target.matches('[data-cart-close]')) closeCart();
    else if (target.matches('[data-reward-open]')) { event.preventDefault(); openRewardDialog(target); }
    else if (target.matches('[data-line-change]')) { const line = state.cart?.items.find((item) => item.key === target.dataset.key); if (line) changeLine(line.key, Math.max(0, line.quantity + Number(target.dataset.lineChange))); }
    else if (target.matches('[data-line-remove]')) changeLine(target.dataset.key, 0);
    else if (target.matches('[data-checkout-button]')) checkout();
    else if (target.matches('[data-builder-remove]')) removeBuilderItem(target.closest('[data-product-form]'));
    else if (target.matches('[data-product-quick-view]')) openProductDialog(target);
  });
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-purchase-mode]') && event.target.checked) applyPurchaseMode(event.target.value, state.purchaseFrequency);
    else if (event.target.matches('[data-purchase-frequency]')) applyPurchaseMode('subscribe', event.target.value);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if ($('[data-reward-dialog]')?.open) { event.preventDefault(); closeRewardDialog(); return; }
    if ($('[data-product-dialog]')?.open) { event.preventDefault(); closeProductDialog(); return; }
    const menu = $('[data-mobile-menu]');
    if (menu && !menu.hidden) { $('[data-menu-close]')?.click(); return; }
    closeCart();
  });
  document.addEventListener('DOMContentLoaded', () => {
    closeCart(false);
    try { state.purchaseFrequency = localStorage.getItem('outlaw_purchase_frequency') || ''; } catch (_) {}
    initMenu(); initCartDrawer(); initRewardDialog(); initBuilder(); initProductForms(); initProductDialog(); initGallery(); initLiveReviewCount(); initHomeHero(); initBrandMarquee(); initFeaturedReviews();
    refreshCart();
  });
  document.addEventListener('shopify:section:load', () => {
    initHomeHero(); initBrandMarquee(); initFeaturedReviews();
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target?.querySelector?.('[data-home-hero]');
    if (root?.outlawHeroStop) root.outlawHeroStop();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const option = event.target?.matches?.('[data-hero-option]') ? event.target : event.target?.querySelector?.('[data-hero-option]');
    if (option) option.click();
  });
})();
